import { Link } from "react-router-dom";
import { Article } from "@/mocks/categories";

interface ArticleCardProps {
  article: Article;
  variant?: "default" | "compact" | "featured";
}

export default function ArticleCard({ article, variant = "default" }: ArticleCardProps) {
  if (variant === "compact") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="flex gap-3 p-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer group"
      >
        <div className="w-24 h-16 flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            title={`每日黑料 - ${article.title}`}
            className="w-full h-full object-cover object-top"
            loading="lazy"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-800 line-clamp-2 group-hover:text-primary-500 transition-colors leading-snug">
            {article.title}
          </h4>
          <div className="flex items-center gap-3 mt-1.5 text-xs text-foreground-500">
            <span className="whitespace-nowrap">{article.publishDate}</span>
            <span className="flex items-center gap-1 whitespace-nowrap">
              <i className="ri-eye-line w-3 h-3 flex items-center justify-center"></i>
              {article.viewCount >= 10000 ? `${(article.viewCount / 10000).toFixed(1)}万` : article.viewCount}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "featured") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="block rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all cursor-pointer group h-full"
      >
        <div className="relative w-full h-64 overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            title={`每日黑料 - ${article.title}`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-5">
            <span className="inline-block px-3 py-1 bg-primary-500 text-background-50 text-xs font-medium rounded-full mb-2 whitespace-nowrap">
              每日黑料大赛
            </span>
            <h3 className="text-white text-xl font-heading font-bold line-clamp-2 leading-snug">
              {article.title}
            </h3>
          </div>
        </div>
        <div className="p-4">
          <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed">{article.excerpt}</p>
          <div className="flex items-center justify-between mt-3 text-xs text-foreground-500">
            <span className="whitespace-nowrap">{article.publishDate}</span>
            <span className="flex items-center gap-1 whitespace-nowrap">
              <i className="ri-eye-line w-3 h-3 flex items-center justify-center"></i>
              {article.viewCount >= 10000 ? `${(article.viewCount / 10000).toFixed(1)}万阅读` : `${article.viewCount}阅读`}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="block rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all cursor-pointer group h-full"
    >
      <div className="relative w-full h-48 overflow-hidden">
        <img
          src={article.coverImage}
          alt={article.title}
          title={`每日黑料 - ${article.title}`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        {article.isHot && (
          <span className="absolute top-3 left-3 px-2 py-0.5 bg-primary-500 text-background-50 text-xs font-medium rounded-full whitespace-nowrap">
            热门
          </span>
        )}
      </div>
      <div className="p-4">
        <div className="flex flex-wrap gap-1.5 mb-2">
          {article.tags.slice(0, 2).map((tag) => (
            <span key={tag} className="px-2 py-0.5 bg-secondary-100 text-secondary-700 text-xs rounded-full whitespace-nowrap">
              {tag}
            </span>
          ))}
        </div>
        <h3 className="text-base font-heading font-bold text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-500 transition-colors mb-2">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed">{article.excerpt}</p>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-background-200/70 text-xs text-foreground-500">
          <span className="whitespace-nowrap">{article.publishDate}</span>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1 whitespace-nowrap">
              <i className="ri-eye-line w-3 h-3 flex items-center justify-center"></i>
              {article.viewCount >= 10000 ? `${(article.viewCount / 10000).toFixed(1)}万` : article.viewCount}
            </span>
            <span className="flex items-center gap-1 whitespace-nowrap">
              <i className="ri-chat-3-line w-3 h-3 flex items-center justify-center"></i>
              {article.commentCount >= 10000 ? `${(article.commentCount / 10000).toFixed(1)}万` : article.commentCount}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}